// 分级卡片 — L1 黄底 / L2·L3 灰底;序号+标题+提示气泡+计数+Tab[逐条添加|批量导入]。
import { useState } from 'react'
import { Lightbulb } from 'lucide-react'
import { Button } from '@dogdisk/shared'
import type { JumpDomainRow } from '../../_lib/jumpdomain-api'
import { DomainRow } from './domain-row'
import { BatchImportTab } from './batch-import-tab'

const LEVEL_STYLE: Record<1 | 2 | 3, { bg: string; title: string; tip: string }> = {
  1: { bg: 'bg-amber-50 border-amber-200', title: '一级跳转 · 入口域名', tip: '非常好记的域名(如 abc.vip)' },
  2: { bg: 'bg-gray-50 border-gray-200', title: '二级跳转 · 中转域名', tip: '比较好记的域名(如 abc-1.vip)' },
  3: { bg: 'bg-gray-50 border-gray-200', title: '三级跳转 · 最终落地', tip: '最终承载流量的落地域名' },
}

export function LevelCard({
  chainId,
  level,
  domains,
  onAddSingle,
  onDeleteDomain,
}: {
  chainId: string
  level: 1 | 2 | 3
  domains: JumpDomainRow[]
  onAddSingle: () => void
  onDeleteDomain: (row: JumpDomainRow) => void
}) {
  const [tab, setTab] = useState<'single' | 'batch'>('single')
  const style = LEVEL_STYLE[level]
  const showKey = level !== 3 // L3 自定义落地不绑 key

  return (
    <div className={`rounded-lg border p-3 ${style.bg}`} data-testid={`Ye71I-level-${level}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-600">
            {level}
          </span>
          <span className="text-sm font-medium text-gray-800">{style.title}</span>
          <span className="text-xs text-gray-500">({domains.length} 个域名)</span>
        </div>
        <div className="flex gap-1 text-xs">
          <button
            type="button"
            onClick={() => setTab('single')}
            className={tab === 'single' ? 'font-semibold text-amber-700' : 'text-gray-400'}
            data-testid={`Ye71I-tab-single-${level}`}
          >
            逐条添加
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={() => setTab('batch')}
            className={tab === 'batch' ? 'font-semibold text-amber-700' : 'text-gray-400'}
            data-testid={`Ye71I-tab-batch-${level}`}
          >
            批量导入
          </button>
        </div>
      </div>

      <p className="mb-2 flex items-center gap-1 text-xs text-amber-600">
        <Lightbulb className="h-3 w-3" />
        {style.tip}
        {!showKey && <span className="ml-2 text-gray-400">无需绑定 API Key</span>}
      </p>

      {tab === 'single' ? (
        <>
          {domains.length === 0 ? (
            <p className="py-2 text-sm text-gray-400">无</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {domains.map((d, i) => (
                <DomainRow key={d.id} row={d} index={i} showKey={showKey} onDelete={onDeleteDomain} />
              ))}
            </ul>
          )}
          <Button size="sm" variant="secondary" onClick={onAddSingle} data-testid={`Ye71I-add-domain-${level}`}>
            + 域名
          </Button>
        </>
      ) : (
        <BatchImportTab chainId={chainId} level={level} testidPrefix={`Ye71I-level-${level}`} />
      )}
    </div>
  )
}
